import type { AnchorHTMLAttributes, ReactElement, ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';

function tryFormatJson(text: string): string | null {
  const t = text.trim();
  if (t.length === 0) return null;
  if (t[0] !== '{' && t[0] !== '[') return null;
  try {
    const parsed: unknown = JSON.parse(t);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
}

// Treat only absolute URLs with a safe scheme as clickable links. Relative
// paths or anchor-only hrefs would otherwise resolve against the Kirin webapp
// origin and 404 when clicked.
function isExternalUrl(href: string | undefined): boolean {
  if (href === undefined) return false;
  return /^(https?:|mailto:|tel:|ftp:)/i.test(href);
}

function isWebUrl(href: string | undefined): boolean {
  if (href === undefined) return false;
  return /^https?:/i.test(href);
}

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  node?: unknown;
  children?: ReactNode;
};

function MarkdownAnchor({
  href,
  children,
  node: _node,
  ...rest
}: AnchorProps): ReactElement {
  if (isExternalUrl(href)) {
    if (isWebUrl(href)) {
      return (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return (
      <a {...rest} href={href}>
        {children}
      </a>
    );
  }
  return <span {...rest}>{children}</span>;
}

const markdownComponents: Components = {
  a: MarkdownAnchor,
};

interface InstructionRenderedBodyProps {
  content: string;
}

/**
 * JSON → pretty-printed block; otherwise markdown via react-markdown (no raw HTML).
 */
export function InstructionRenderedBody({
  content,
}: InstructionRenderedBodyProps): ReactElement {
  const jsonFormatted = tryFormatJson(content);
  if (jsonFormatted !== null) {
    return (
      <pre className="overflow-auto whitespace-pre font-mono text-xs leading-relaxed">
        <code>{jsonFormatted}</code>
      </pre>
    );
  }

  return (
    <div className="instruction-md text-sm [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:font-semibold [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_ul]:list-disc [&_ul]:pl-5">
      <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
    </div>
  );
}
